import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import Swal from "sweetalert2";
import { GRADIENTS } from "utils/theme";
import "./currency-converter.css";

const API_BASE = "https://backend.vendispot.wise-hustlers.com/api/v1/admin/auth";

const formatNumber = (value, options = {}) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
    ...options
  }).format(value);
};

function CurrencyConverter() {
  const [exchangeRate, setExchangeRate] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);


  // ✅ Fetch current exchange rate
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const token = localStorage.getItem("vendispot_token");
        console.log("TOKEN:", localStorage.getItem("vendispot_token"));

        const response = await fetch(
          `${API_BASE}/getExchangeRate`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (data?.success && data?.data?.exchangeRate) {
          setExchangeRate(String(data.data.exchangeRate));
        } else {
          setExchangeRate("");
        }
      } catch (error) {
        console.error("Exchange rate fetch failed");
        setExchangeRate("");
      } finally {
        setFetching(false);
      }
    };

    fetchRate();
  }, []);

  // ✅ Save Exchange Rate
 const handleSave = async () => {
  const rate = Number(exchangeRate);

  if (!rate || rate <= 0) {
    Swal.fire({
      icon: "error",
      title: "Invalid Value",
      text: "Please enter a valid exchange rate (greater than 0)",
    });
    return;
  }

  setLoading(true);

  try {
    const token = localStorage.getItem("vendispot_token");

    const response = await fetch(
      `${API_BASE}/updateExchangeRate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ exchangeRate: rate }),
      }
    );

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message);
    }

    Swal.fire({
      icon: "success",
      title: "Updated Successfully",
      text: "Exchange rate updated successfully",
    });

    // ✅ Clear input after save

  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.message || "Failed to update exchange rate",
    });
  } finally {
    setLoading(false);
  }
};

  // ✅ Auto inverse calculation
  const inverseRate = useMemo(() => {
    const rate = Number(exchangeRate);
    if (!rate) return 0;
    return 1 / rate;
  }, [exchangeRate]);

  return (
    <Container fluid className="py-4">
      <Row>
        <Col md="12">
          <Card className="shadow border-0 rounded-3 overflow-hidden">
            
            {/* HEADER */}
            <div
              style={{ background: GRADIENTS.primary }}
              className="text-white py-3 px-4"
            >
              <h4 className="mb-0 fw-semibold">
                Exchange Rate Setup
              </h4>
            </div>

            <Card.Body>
              {fetching ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <div className="p-4 bg-light rounded-3 border">
                  
                  <h6 className="mb-3 fw-semibold">
                    Set Exchange Rate (Admin Only)
                  </h6>

                  <Form.Group>
                    <Form.Label>1 USD = ? LRD</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(e.target.value)}
                      placeholder="Enter LRD value"
                    />
                  </Form.Group>

                  {/* Live Preview */}
                  <div className="mt-3 text-muted small">
                    👉 1 USD = {formatNumber(Number(exchangeRate || 0))} LRD  
                    <br />
                    👉 1 LRD ={" "}
                    {formatNumber(inverseRate, {
                      maximumFractionDigits: 6,
                    })}{" "}
                    USD
                  </div>

                  <div className="mt-4">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Exchange Rate
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>

          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default CurrencyConverter;